/**
 * Simple rate limiter utility to prevent abuse of game mechanics
 * Uses a Map to track requests by key (userId or saveId) and enforce limits
 */

// Store request timestamps by key and action type
const requestMap = new Map();

// Default rate limits for different actions (in milliseconds)
const DEFAULT_LIMITS = {
  'mining_rig_purchase': 2000,  // 2 seconds between mining rig purchases
  'save_update': 500,          // 500ms between save updates
  'save_creation': 5000,       // 5 seconds between save creations
  'generic': 1000              // 1 second for any other action
};

/**
 * Check if a request should be rate limited
 * @param {string} key - The unique key for the requester (userId or saveId)
 * @param {string} actionType - The type of action being performed
 * @returns {boolean} True if the request should be allowed, false if it should be rate limited
 */
export function checkRateLimit(key, actionType = 'generic') {
  if (!key) return false;
  
  const mapKey = `${key}:${actionType}`;
  const now = Date.now();
  
  // Get the appropriate rate limit
  const limitMs = DEFAULT_LIMITS[actionType] || DEFAULT_LIMITS.generic;
  
  // Check if this key+action has a recent request
  const lastRequest = requestMap.get(mapKey);
  
  if (lastRequest) {
    const timeSinceLastRequest = now - lastRequest;
    
    // If not enough time has passed, rate limit the request
    if (timeSinceLastRequest < limitMs) {
      return {
        allowed: false,
        retryAfter: Math.ceil((limitMs - timeSinceLastRequest) / 1000)
      };
    }
  }
  
  // Update the timestamp for this request
  requestMap.set(mapKey, now);
  
  // Clean up old entries periodically to prevent memory leaks
  if (Math.random() < 0.1) {  // 10% chance on each call to clean up
    cleanupOldEntries();
  }
  
  return { allowed: true };
}

/**
 * Clean up old entries from the request map to prevent memory leaks
 * Removes entries older than 1 hour
 */
function cleanupOldEntries() {
  const now = Date.now();
  const oneHour = 60 * 60 * 1000;
  
  for (const [key, timestamp] of requestMap.entries()) {
    if (now - timestamp > oneHour) {
      requestMap.delete(key);
    }
  }
} 